import { Alert, Anchor, Stack } from '@mantine/core';
import { IconAlertTriangle, IconPlugConnectedX } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { isAppUninstalled, SHOPIFY_APP_STORE_URL } from '../helpers/domainSuspension';
import useFetch from '../helpers/useFetch';
import ErrorMessage from './ErrorMessage';

const ServiceDisabledAlert = () => {
  const fetch = useFetch();
  const { domain } = useSelector((state) => state.domain);
  // The 'email' disabled-service is Releva's own deliverability kill switch (bounce
  // rate) - it only gates Releva's transports. A Klaviyo-connected domain delivers
  // through Klaviyo and the send path ignores this flag, so the alert would be false.
  // While the Klaviyo state for this domain is still loading, the email warning is
  // held back rather than flashed (the other warnings are unaffected).
  const { account: klaviyoAccount, loading: klaviyoLoading } = useSelector((state) => state.klaviyo);
  const emailManagedByReleva = klaviyoLoading === false && !klaviyoAccount;

  const [disabledServices, setDisabledServices] = useState();
  const [error, setError] = useState();

  useEffect(() => {
    let isComponentUnmounted = false;

    fetch(`/api/admin/accounts/${domain.accountId}/domains/${domain.id}/disabledServices`)
      .then((res) => {
        if (isComponentUnmounted) {
          return;
        }

        if (res.message) {
          throw new Error(res.message);
        }

        setDisabledServices(res);
      })
      .catch(setError);

    return () => {
      isComponentUnmounted = true;
    };
  }, [domain.id, domain.accountId, fetch]);

  if (error) {
    return <ErrorMessage message={error.message} />;
  }

  const alerts = [];
  // Same stopped state as below, but a cause the merchant owns and can undo, so
  // it gets the action that actually resolves it. Support cannot re-enable a
  // domain whose app is gone from the store - only reinstalling can - and
  // telling them to email us instead is a round trip that ends in the same
  // advice.
  if (isAppUninstalled(domain)) {
    alerts.push(
      <Alert color="red" icon={<IconPlugConnectedX size={18} />} key="appUninstalled">
        The Releva app has been removed from your store, so campaigns, workflows and web personalizations have stopped
        and your subscription is no longer active. Reinstall it from the{' '}
        <Anchor href={SHOPIFY_APP_STORE_URL} target="_blank" rel="noopener noreferrer">
          Shopify App Store
        </Anchor>{' '}
        to pick up where you left off, or reach out to{' '}
        <Anchor href="mailto:support@releva.ai">support@releva.ai</Anchor> if this was not intentional.
      </Alert>
    );
  } else if (!domain.running) {
    alerts.push(
      <Alert color="red" icon={<IconAlertTriangle size={18} />} key="domain">
        Shop is disabled. Campaigns, workflow, or web personalizations will not run. Please reach out to{' '}
        <Anchor href="mailto:support@releva.ai">support@releva.ai</Anchor> to re-enable it.
      </Alert>
    );
  } else if (domain.deliveryActive === false) {
    // Value delivery paused (usage over limit past the grace period). Distinct
    // from a full disable: data collection keeps running.
    alerts.push(
      <Alert color="red" icon={<IconAlertTriangle size={18} />} key="deliveryPaused">
        Your campaigns and on-site personalization are paused because your usage exceeded your plan limit. Data
        collection is still running, so no tracking data is lost. Raise your limit or reach out to{' '}
        <Anchor href="mailto:support@releva.ai">support@releva.ai</Anchor> to resume — service also resumes
        automatically at the start of your next billing period.
      </Alert>
    );
  } else if (domain.deliveryPauseAt) {
    // Over the limit but still within the grace window before delivery is paused.
    // deliveryPauseAt is the API-computed pause date (single source of truth for
    // the grace window). Guard against a malformed value so we never render
    // "Invalid Date".
    const pauseAt = new Date(domain.deliveryPauseAt);
    if (!Number.isNaN(pauseAt.getTime())) {
      // A pause date in the past is ambiguous: the backend pause job may be late,
      // or (far more common) the merchant already raised their limit. A raise
      // resumes delivery immediately, but the backend deliberately preserves the
      // over-limit streak until the nightly usage job settles it against an
      // honest usage snapshot (see resumeUsageDelivery in magellan-api's
      // cap-state manager), so this banner keeps showing for up to a day after
      // the merchant acted. The component cannot tell the causes apart, so never
      // claim the pause is "imminent" - say "soon" and tell merchants who have
      // already acted that the warning clears on its own, instead of telling
      // them their service is about to stop right after they fixed it.
      const pauseDatePassed = pauseAt.getTime() <= Date.now();
      const pauseTiming = pauseDatePassed ? 'soon' : `around ${pauseAt.toLocaleDateString(navigator.language)}`;
      alerts.push(
        <Alert color="yellow" icon={<IconAlertTriangle size={18} />} key="overLimit">
          Your usage is over your plan limit. If it stays over the limit, your campaigns and on-site personalization
          will be paused {pauseTiming}. Raise your limit or reach out to{' '}
          <Anchor href="mailto:support@releva.ai">support@releva.ai</Anchor> to avoid interruption. Data collection is
          unaffected.
          {pauseDatePassed && ' If you have already raised your limit, this warning should clear within a day.'}
        </Alert>
      );
    }
  }

  if (disabledServices?.includes('email') && emailManagedByReleva) {
    alerts.push(
      <Alert color="red" icon={<IconAlertTriangle size={18} />} key="email">
        Email sending is disabled. Please reach out to support@releva.ai in order to enable it.
      </Alert>
    );
  }

  if (disabledServices?.includes('whatsappMessage')) {
    alerts.push(
      <Alert color="red" icon={<IconAlertTriangle size={18} />} key="whatsappMessage">
        Whatsapp Message sending is disabled due to error code. Please reach out to support@releva.ai for details.
      </Alert>
    );
  }

  if (disabledServices?.includes('textMessage')) {
    alerts.push(
      <Alert color="red" icon={<IconAlertTriangle size={18} />} key="textMessage">
        Text Message sending is disabled. Please reach out to support@releva.ai for details.
      </Alert>
    );
  }

  if (alerts.length === 0) {
    return null;
  }

  return (
    <Stack gap="xs" mb="md">
      {alerts}
    </Stack>
  );
};

export default ServiceDisabledAlert;
